<template>
    <div class="bsdc:mx-auto max-w-small bsdc:relative">
        <div class="bsdc:mx-auto max-w-large bsdc:text-center bsdc:space-y-4 bsdc:mb-10">
            <h3 class="bsdc:subtitle">
                Ring Summary
            </h3>

            <h2 class="bsdc:heading bsdc:text-[1.5rem] bsdc:sm:text-[2rem] bsdc:uppercase">
                Confirm Your Purchase
            </h2>
        </div>

        <div class="bsdc:relative bsdc:pt-7 bsdc:sm:pt-15 bsdc:px-5 bsdc:sm:px-15">
            <div
                class="bsdc:w-full bsdc:h-[310px] bsdc:absolute bsdc:top-0 bsdc:left-0 bsdc:bg-2-1 bsdc:rounded-lg bsdc:z-10">
            </div>

            <div class="bsdc:mx-auto bsdc:max-w-[440px] bsdc:relative bsdc:z-10 bsdc:space-y-7 bsdc:sm:space-y-15">
                <div class="bsdc:space-y-6">
                    <div class="form-row bsdc:flex bsdc:flex-col bsdc:gap-2">
                        <label
                            class="bsdc:text-[13px] bsdc:font-semibold"
                            for="configurator-ring-engraving"
                        >
                            Engrave Your ring
                            <span class="bsdc:font-normal">
                                (+ €30)
                            </span>
                        </label>

                        <input
                            id="configurator-ring-engraving"
                            class="input"
                            type="text"
                            name="engraving"
                            placeholder="What would you like it to say?"
                            maxlength="30"
                            v-model="engraving"
                            @input="$emit('update:engraving', engraving)"
                        />

                        <p class="bsdc:text-[11px]">
                            30 characters max
                        </p>
                    </div>
                </div>

                <div
                    class="bsdc:bg-white bsdc:border bsdc:border-1-2/20 bsdc:rounded-lg bsdc:shadow-2xl bsdc:p-5 bsdc:sm:p-7.5">
                    <p
                        v-if="summaryTitle"
                        class="bsdc:text-base"
                    >
                        {{ summaryTitle }}
                    </p>

                    <p
                        v-if="ringMetal || ringSize"
                        class="bsdc:text-[13px] bsdc:mt-2"
                    >
                        {{ ringMetal }} / {{ ringSize }}
                    </p>

                    <div
                        v-if="props.ringImageUrl || props.diamondData.image_urls.length"
                        class="config-summary__media bsdc:my-4 bsdc:sm:my-7"
                    >
                        <img
                            v-if="props.ringImageUrl && props.diamondData.image_urls.length < 1"
                            :src="props.ringImageUrl"
                            alt=""
                            class="bsdc:w-full bsdc:block bsdc:rounded-md"
                        />

                        <Carousel
                            v-else
                            :image-url="props.diamondData.image_url"
                            :image-urls="props.diamondData.image_urls"
                        />
                    </div>

                    <p
                        v-if="totalPrice"
                        class="bsdc:font-semibold bsdc:text-base bsdc:mb-1 bsdc:sm:mb-2"
                    >
                        {{ formatMoney(totalPrice) }}
                    </p>

                    <div
                        v-if="hummEnabled"
                        id="summary-widget"
                        class="bsdc:[&_*]:text-[13px] bsdc:mb-5"
                    >
                        <div id="humm-widget"></div>
                    </div>

                    <DataList
                        :data="selectedDiamondData"
                        :version="3"
                        size="small"
                        class="bsdc:border-t bsdc:border-2-1 bsdc:pt-5 bsdc:mt-5"
                    />

                    <div
                        v-if="deliveryNote"
                        class="bsdc:p-3 bsdc:bg-2-1 bsdc:rounded-md bsdc:mt-5 bsdc:mb-5"
                    >
                        <p
                            v-html="deliveryNote"
                            class="bsdc:text-[13px]"
                        ></p>
                    </div>

                    <div class="config-summary__action">
                        <button
                            class="button bsdc:w-full"
                            @click.prevent="emits('addToCart')"
                        >
                            Add To Cart
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from "vue";

    import { formatMoney } from "@js/helpers";

    import Carousel from "@js/Components/Carousel.vue";
    import DataList from "@js/Components/DataList.vue";

    const props = defineProps({
        configuratorSettings: Object,
        ringImageUrl: String,
        diamondData: Object,
        totalPrice: Number,
    });

    const emits = defineEmits([
        "update:engraving",
        "addToCart",
    ]);

    const stones = computed(() => props.configuratorSettings.stones);
    const summaryTitle = ref(BSDC.title);
    const hummEnabled = computed(() => props.configuratorSettings.hummEnabled);

    const ringMetal = ref("");
    const ringSize = ref("");
    const engraving = ref("");

    const deliveryNote = computed(() => {
        return document.querySelector(".woocommerce-variation-description")?.textContent || "";
    });

    const selectedDiamondData = computed(() => {
        if (!props.diamondData) {
            return [];
        }

        const isDiamond = stones.value.includes('diamond');
        const waitTimeValue = props.diamondData.wait_time && props.diamondData.wait_time !== 'None'
            ? `There is a wait time on this diamond of <em>${props.diamondData.wait_time}</em>.`
            : null;

        const commonFields = [
            { label: 'Metal', value: ringMetal.value },
            { label: 'Size', value: ringSize.value },
            { label: 'Shape', value: props.diamondData.shape_formatted },
            { label: 'Centre Carat', value: props.diamondData.weight_formatted },
            { label: 'Total Carat', value: props.diamondData.setting_weight_formatted },
            { label: 'Colour', value: props.diamondData.color },
            { label: 'Clarity', value: props.diamondData.clarity },
            { label: 'Spread', value: props.diamondData.spread },
            { label: 'Cut', value: props.diamondData.cut },
            { label: 'Cert', value: props.diamondData.certificate_body },
            { label: 'Cert Number', value: props.diamondData.certificate_number },
            { label: 'Engraving', value: engraving.value },
            { label: 'Important', value: waitTimeValue },
        ];

        if (!isDiamond) {
            return [
                { label: 'Centre Stone', value: `${props.diamondData.type_formatted} Grown Gemstone` },
                { label: 'Gemstone Species', value: props.diamondData.stone },
                ...commonFields,
            ];
        }

        return [
            { label: 'Diamond Type', value: props.diamondData.type_formatted },
            ...commonFields,
            { label: 'Polish', value: props.diamondData.polish },
            { label: 'Symmetry', value: props.diamondData.symmetry },
        ];
    });

    onMounted(() => {
        const ringMetalSelect = document.querySelector('.variations_form select[name="attribute_pa_ring-metal"]') || document.querySelector('.variations_form select[name="attribute_pa_metal"]');
        const ringSizeSelect = document.querySelector('.variations_form select[name="attribute_pa_ring-size"]') || document.querySelector('.variations_form select[name="attribute_pa_size"]');

        ringMetal.value = ringMetalSelect ? ringMetalSelect.options[ringMetalSelect.selectedIndex].text : null;
        ringSize.value = ringSizeSelect ? ringSizeSelect.options[ringSizeSelect.selectedIndex].text : null;
    });
</script>
